/**
 * 通用的模板渲染器
 * 合并 get.ts 和 post.ts 中的重复渲染逻辑
 */
import { MethodsType, GetSchema } from '@/define';
import { Helper, renderMethodArgs } from '@/methods/base';
import { wrapperMethodPreInterface } from '@/methods/generator-interface';
import Store from '@/store';

/**
 * 渲染 TypeScript 或 JavaScript 接口函数
 */
export const renderMethodTemplate = (instance: Helper, includeData: boolean = false) => {
  const pathArgs = instance.getMethodPrePath();
  const paramsArgs = instance.getMethodPreParams();
  const dataArgs = includeData ? instance.renderGetMethodData() : '';
  const optionArgs = instance.getMethodOption();
  
  const args = renderMethodArgs([pathArgs, paramsArgs, dataArgs, optionArgs]);
  
  const requestArgs = renderMethodArgs([
    `url: \`${instance.getUrl()}\``,
    `method: \'${instance.method}\'`,
    `${instance.renderOptionsStr.data}`,
    `${instance.renderOptionsStr.params}`,
    `...options`,
  ]);
  
  const preInterface = renderMethodArgs(wrapperMethodPreInterface(instance));
  const summary = instance.description === instance.summary ? '' : `\n           * @summary ${instance.summary}`;
  
  // TypeScript 模式
  if (Store.isTypeScript) {
    return `
          /***
           * @description  ${instance.description}${summary}
           **/
          export const ${instance.getMethodsName()} = <${preInterface}>(${args}):Promise<S> => {
              return axios.request({${requestArgs}})
          }\n
          `;
  }
  
  // JsDoc 模式
  if (Store.isJsDoc) {
    return `
          /***
           * @type {import("./${Store.filename}.types.ts").${instance.getMethodsName()}}
           * @description  ${instance.description}${summary}
           **/
          export const ${instance.getMethodsName()} = (${args}) => {
              return axios.request({${requestArgs}})
          }\n
        `;
  }
  
  // 纯 JavaScript 模式
  return `
    /***
           * @description  ${instance.description}${summary}
           **/
          export const ${instance.getMethodsName()} = (${args}) => {
              return axios.request({${requestArgs}})
          }\n
          `;
};

/**
 * 渲染 JsDoc 类型定义
 */
export const renderJsDocTemplate = (instance: Helper, includeData: boolean = false) => {
  const pathArgs = instance.getMethodPrePath();
  const paramsArgs = instance.getJsDocMethodPreParams();
  const dataArgs = includeData ? instance.renderJsDocGetMethodData() : '';
  const optionArgs = instance.getJsDocMethodOption();
  
  const args = renderMethodArgs([pathArgs, paramsArgs, dataArgs, optionArgs]);
  const preInterface = renderMethodArgs(wrapperMethodPreInterface(instance));
  const response = 'S';
  
  return `
    export type ${instance.getMethodsName()} = <${preInterface}>(${args})=> Promise<${response}>\n
  `;
};

/**
 * 创建方法渲染器
 * @param uri - 接口路径
 * @param method - 请求方法
 * @param params - 接口参数定义
 * @param includeData - 是否包含 data 参数（POST/PUT/DELETE 需要）
 */
export const createMethodRenderer = (
  uri: string,
  method: MethodsType,
  params: GetSchema,
  includeData: boolean = false
) => {
  const instance = new Helper(uri, method, params);
  
  // 渲染模板方法，可以通过传入自定义的方法覆盖
  const renderTemplate = (renderCallback?: Function): string => {
    if (typeof renderCallback === 'function') {
      return renderCallback.call(instance);
    }
    return renderMethodTemplate(instance, includeData);
  };
  
  // 渲染 JsDoc 模板
  const renderJsDocMethod = (renderJsDocCallback?: Function): string => {
    if (typeof renderJsDocCallback === 'function') {
      return renderJsDocCallback.call(instance);
    }
    return renderJsDocTemplate(instance, includeData);
  };
  
  return {
    instance,
    renderTemplate,
    renderJsDocTemplate: renderJsDocMethod,
  };
};

